import React from "react";
import {
  ActivityIndicator,
  Pressable,
  Switch,
  Text,
  TextInput,
  View,
} from "react-native";
import Ionicons from "react-native-vector-icons/Ionicons";

import type {
  TCommunicationsAssignedPhoneNumberItem,
  TCommunicationsClinicUserAccess,
} from "../../../../../services/api-communications";
import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import { formatPhoneNumberDisplay } from "../../../../../utils/phoneNumber";
import { PrimaryButton } from "../../../../common/native";
import { styles } from "../HomeConversations.styles";

export type TInboundRoutingMode = "phone_only" | "webapp_only" | "simultaneous";

type InboundCallRoutingCardProps = {
  isConfigTab: boolean;
  assignedPhoneNumbers: TCommunicationsAssignedPhoneNumberItem[];
  selectedPhoneNumberId: number | null;
  onSelectPhoneNumberId: (phoneNumberId: number) => void;
  routingLoading: boolean;
  routingSaving: boolean;
  routingError: string | null;
  mode: TInboundRoutingMode;
  enabled: boolean;
  phoneTargetCountryCode: string;
  phoneTargetLocalNumber: string;
  clinicUserUuid: string;
  timeoutSeconds: string;
  selectableClinicUsers: TCommunicationsClinicUserAccess[];
  fieldErrors: {
    phoneTarget?: string;
    clinicUserUuid?: string;
    timeoutSeconds?: string;
    save?: string;
  };
  onEnabledChange: (enabled: boolean) => void;
  onModeChange: (mode: TInboundRoutingMode) => void;
  onPhoneTargetCountryCodeChange: (value: string) => void;
  onPhoneTargetLocalNumberChange: (value: string) => void;
  onClinicUserUuidChange: (value: string) => void;
  onTimeoutSecondsChange: (value: string) => void;
  onRetryLoad: () => void;
  onSave: () => void;
};

const modeOptions: Array<{
  value: TInboundRoutingMode;
  label: string;
}> = [
  { value: "phone_only", label: "Phone Only" },
  { value: "webapp_only", label: "Webapp Only" },
  { value: "simultaneous", label: "Simultaneous" },
];

const InboundCallRoutingCard: React.FC<InboundCallRoutingCardProps> = ({
  isConfigTab,
  assignedPhoneNumbers,
  selectedPhoneNumberId,
  onSelectPhoneNumberId,
  routingLoading,
  routingSaving,
  routingError,
  mode,
  enabled,
  phoneTargetCountryCode,
  phoneTargetLocalNumber,
  clinicUserUuid,
  timeoutSeconds,
  selectableClinicUsers,
  fieldErrors,
  onEnabledChange,
  onModeChange,
  onPhoneTargetCountryCodeChange,
  onPhoneTargetLocalNumberChange,
  onClinicUserUuidChange,
  onTimeoutSecondsChange,
  onRetryLoad,
  onSave,
}) => {
  const [showModeInfo, setShowModeInfo] = React.useState<boolean>(false);
  const selectedPhoneNumber = assignedPhoneNumbers.find(
    (phoneNumber) => phoneNumber.phone_number_id === selectedPhoneNumberId,
  );
  const phoneTargetRequired = mode === "phone_only" || mode === "simultaneous";
  const clinicUserRequired = mode === "webapp_only" || mode === "simultaneous";
  const controlsDisabled =
    routingLoading || routingSaving || !selectedPhoneNumberId;

  return (
    <View style={[styles.card, !isConfigTab && styles.hiddenSection]}>
      <View style={styles.rowBetween}>
        <Text style={styles.cardTitle}>Inbound Call Routing</Text>
        {(routingLoading || routingSaving) && (
          <ActivityIndicator size="small" color={ADMIN_TOKENS.color.accent} />
        )}
      </View>

      {!assignedPhoneNumbers.length ? (
        <Text style={styles.emptyText}>
          Assign a clinic number first to configure inbound routing.
        </Text>
      ) : (
        <>
          <Text style={styles.inputLabel}>Assigned Number</Text>
          <View style={styles.routingOptionWrap}>
            {assignedPhoneNumbers.map((phoneNumber) => {
              const isSelected =
                selectedPhoneNumberId === phoneNumber.phone_number_id;
              const formattedE164 = formatPhoneNumberDisplay(phoneNumber.e164, {
                fallback: phoneNumber.e164 || "number",
              });
              return (
                <Pressable
                  key={phoneNumber.phone_number_id}
                  style={[
                    styles.inlineOptionButton,
                    styles.routingOptionButton,
                    isSelected && styles.inlineOptionButtonActive,
                  ]}
                  onPress={() =>
                    onSelectPhoneNumberId(phoneNumber.phone_number_id)
                  }
                  disabled={routingSaving}
                  accessibilityLabel={`Select ${formattedE164} for inbound routing`}
                >
                  <Text
                    style={[
                      styles.inlineOptionText,
                      isSelected && styles.inlineOptionTextActive,
                    ]}
                  >
                    {formattedE164}
                  </Text>
                  {phoneNumber.is_primary ? (
                    <Text style={styles.metaText}>Primary</Text>
                  ) : null}
                </Pressable>
              );
            })}
          </View>

          {routingError ? (
            <View style={styles.sectionStack}>
              <Text style={styles.inlineErrorText}>{routingError}</Text>
              <Pressable
                style={[
                  styles.callAgainButton,
                  controlsDisabled && styles.callAgainButtonDisabled,
                ]}
                onPress={onRetryLoad}
                disabled={controlsDisabled}
                accessibilityLabel="Retry loading inbound routing configuration"
              >
                <Text style={styles.callAgainButtonText}>Retry</Text>
              </Pressable>
            </View>
          ) : null}

          {!routingError ? (
            <>
              <View style={styles.routingSwitchRow}>
                <Text style={styles.inputLabel}>Enabled</Text>
                <Switch
                  value={enabled}
                  onValueChange={onEnabledChange}
                  disabled={controlsDisabled}
                  trackColor={{
                    false: ADMIN_TOKENS.color.borderInput,
                    true: ADMIN_TOKENS.color.accent,
                  }}
                  thumbColor={ADMIN_TOKENS.color.surface}
                  accessibilityLabel="Toggle inbound call routing enabled"
                />
              </View>

              <View style={styles.routingModeHeaderRow}>
                <Text style={styles.inputLabel}>Routing Mode</Text>
                <Pressable
                  style={styles.routingInfoButton}
                  onPress={() =>
                    setShowModeInfo((currentValue) => !currentValue)
                  }
                  accessibilityLabel="Explain inbound routing modes"
                >
                  <Ionicons
                    name="information-circle-outline"
                    size={16}
                    color={ADMIN_TOKENS.color.textSecondary}
                  />
                  <Text style={styles.routingInfoButtonText}>
                    What is this?
                  </Text>
                </Pressable>
              </View>
              {showModeInfo ? (
                <View style={styles.routingInfoPanel}>
                  <Text style={styles.metaText}>
                    Phone Only: ring only the phone target number.
                  </Text>
                  <Text style={styles.metaText}>
                    Webapp Only: ring only the selected clinic user in the
                    webapp.
                  </Text>
                  <Text style={styles.metaText}>
                    Simultaneous: ring phone target and webapp user at the same
                    time.
                  </Text>
                </View>
              ) : null}
              <View style={styles.routingOptionWrap}>
                {modeOptions.map((option) => {
                  const isSelected = mode === option.value;
                  return (
                    <Pressable
                      key={option.value}
                      style={[
                        styles.inlineOptionButton,
                        styles.routingOptionButton,
                        isSelected && styles.inlineOptionButtonActive,
                        controlsDisabled && styles.callAgainButtonDisabled,
                      ]}
                      onPress={() => onModeChange(option.value)}
                      disabled={controlsDisabled}
                      accessibilityLabel={`Set inbound routing mode to ${option.label}`}
                    >
                      <Text
                        style={[
                          styles.inlineOptionText,
                          isSelected && styles.inlineOptionTextActive,
                        ]}
                      >
                        {option.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>

              {phoneTargetRequired ? (
                <>
                  <Text style={styles.inputLabel}>
                    Phone to route calls to:
                  </Text>
                  <View style={styles.dialerInlineToInputs}>
                    <TextInput
                      value={phoneTargetCountryCode}
                      onChangeText={onPhoneTargetCountryCodeChange}
                      placeholder="+1"
                      placeholderTextColor={ADMIN_TOKENS.color.textMuted}
                      style={[styles.input, styles.dialerToCountryCodeInput]}
                      keyboardType="phone-pad"
                      autoCapitalize="none"
                      autoCorrect={false}
                      editable={!controlsDisabled}
                      accessibilityLabel="Inbound phone target country code"
                    />
                    <TextInput
                      value={phoneTargetLocalNumber}
                      onChangeText={onPhoneTargetLocalNumberChange}
                      placeholder="4155550123"
                      placeholderTextColor={ADMIN_TOKENS.color.textMuted}
                      style={[styles.input, styles.dialerToLocalInput]}
                      keyboardType="phone-pad"
                      autoCapitalize="none"
                      autoCorrect={false}
                      editable={!controlsDisabled}
                      accessibilityLabel="Inbound phone target local number"
                    />
                  </View>
                  {fieldErrors.phoneTarget ? (
                    <Text style={styles.inlineErrorText}>
                      {fieldErrors.phoneTarget}
                    </Text>
                  ) : null}
                </>
              ) : null}

              {clinicUserRequired ? (
                <>
                  <Text style={styles.inputLabel}>Clinic User (UUID)</Text>
                  {!selectableClinicUsers.length ? (
                    <Text style={styles.emptyText}>
                      No clinic users are available for webapp routing.
                    </Text>
                  ) : (
                    <View style={styles.routingUserList}>
                      {selectableClinicUsers.map((user) => {
                        const isSelected = clinicUserUuid === user.user_uuid;
                        return (
                          <Pressable
                            key={`${user.clinic_user_id}-${user.user_uuid}`}
                            style={[
                              styles.routingUserOption,
                              isSelected && styles.inlineOptionButtonActive,
                              controlsDisabled &&
                                styles.callAgainButtonDisabled,
                            ]}
                            onPress={() =>
                              onClinicUserUuidChange(user.user_uuid)
                            }
                            disabled={controlsDisabled}
                            accessibilityLabel={`Select clinic user ${user.user_uuid} for inbound webapp routing`}
                          >
                            <Text
                              style={[
                                styles.callNumberText,
                                isSelected && styles.inlineOptionTextActive,
                              ]}
                            >
                              {user.user_uuid}
                            </Text>
                            <Text style={styles.metaText}>
                              {user.role}
                              {user.communications_enabled
                                ? " • access enabled"
                                : " • access will be enabled on save"}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  )}
                  {fieldErrors.clinicUserUuid ? (
                    <Text style={styles.inlineErrorText}>
                      {fieldErrors.clinicUserUuid}
                    </Text>
                  ) : null}
                </>
              ) : null}

              <Text style={styles.inputLabel}>Timeout Seconds (1-120)</Text>
              <TextInput
                value={timeoutSeconds}
                onChangeText={onTimeoutSecondsChange}
                placeholder="30"
                placeholderTextColor={ADMIN_TOKENS.color.textMuted}
                style={styles.input}
                keyboardType="number-pad"
                autoCapitalize="none"
                autoCorrect={false}
                editable={!controlsDisabled}
                accessibilityLabel="Inbound routing timeout in seconds"
              />
              {fieldErrors.timeoutSeconds ? (
                <Text style={styles.inlineErrorText}>
                  {fieldErrors.timeoutSeconds}
                </Text>
              ) : null}

              <Text style={styles.metaText}>
                Webapp routing requires the selected user to be online with an
                active browser subscriber registration.
              </Text>

              {fieldErrors.save ? (
                <Text style={styles.inlineErrorText}>{fieldErrors.save}</Text>
              ) : null}

              <PrimaryButton
                text="Save Routing"
                onPress={onSave}
                loading={routingSaving}
                disabled={controlsDisabled || !selectedPhoneNumber}
                fullWidth
              />
            </>
          ) : null}
        </>
      )}
    </View>
  );
};

export default InboundCallRoutingCard;
